import { useState, useEffect, useRef, useCallback } from 'react';
import { isEqual } from 'lodash';

export const useChangeTracking = (data) => {
    const [hasChanges, setHasChanges] = useState(false);
    const [initialData, setInitialData] = useState(null);
    const initialDataRef = useRef(null);

    // Set initial data when first provided
    useEffect(() => {
        if (data !== null && initialDataRef.current === null) {
            const serializedData = JSON.parse(JSON.stringify(data));
            initialDataRef.current = serializedData;
            setInitialData(serializedData);
            setHasChanges(false);
        }
    }, [data]);

    // Check for changes when data updates
    useEffect(() => {
        if (data !== null && initialDataRef.current !== null) {
            const hasChanged = !isEqual(data, initialDataRef.current);
            setHasChanges(hasChanged);
        }
    }, [data]);

    // Reset tracking to current data
    const resetTracking = useCallback(() => {
        if (data !== null) {
            const serializedData = JSON.parse(JSON.stringify(data));
            initialDataRef.current = serializedData;
            setInitialData(serializedData);
            setHasChanges(false);
        }
    }, [data]);

    // Get specific changed fields
    const getChangedFields = useCallback(() => {
        if (!data || !initialDataRef.current) return [];

        const changes = [];
        const checkObject = (current, initial, path = '') => {
            Object.keys(current).forEach(key => {
                const currentPath = path ? `${path}.${key}` : key;
                const currentValue = current[key];
                const initialValue = initial[key];

                if (typeof currentValue === 'object' && currentValue !== null && !Array.isArray(currentValue)) {
                    checkObject(currentValue, initialValue || {}, currentPath);
                } else if (!isEqual(currentValue, initialValue)) {
                    changes.push(currentPath);
                }
            });
        };

        checkObject(data, initialDataRef.current);
        return changes;
    }, [data]);

    return {
        hasChanges,
        initialData,
        resetTracking,
        getChangedFields
    };
};
